import React from 'react'
import { useParams } from 'react-router-dom'
import Grid from '@material-ui/core/Grid'
import Divider from '@material-ui/core/Divider'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Container from '@material-ui/core/Container'
import Paper from '@material-ui/core/Paper'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { useAuth } from 'lib/auth'
import { formatDateRange, eventsRange } from 'lib/family-cup-utils/event-dates'
import JrlMerchCarousel from 'components/junior-league/merch-carousel/JrlMerchCarousel'
import { MY_PGA_APPLE_APP_STORE, MY_PGA_GOOGLE_PLAY_STORE, NODE_ENV } from 'env'
import AppStoreBadge from 'images/store-badges/app-store-badge.svg'
import GooglePlayBadge from 'images/store-badges/google-play-badge.png'

const useStyles = makeStyles(theme => ({
  grid: {
    width: '100%',
    minWidth: 0,
    '& > .MuiGrid-item': {
      minWidth: 0,
      maxWidth: '100%',
    },
  },
  badge: {
    height: '54px',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  badgeContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
}))

const safeIsDevPreview = () => {
  // Keep this route "local/dev only" so it doesn't get accidentally shared.
  // `react-scripts` may set NODE_ENV=development even when using staging-like hosts,
  // so we also allow `.dev.` hostnames.
  const origin = window?.location?.origin
  return origin?.includes('.dev.') || NODE_ENV === 'development'
}

const buildRegisterProgram = () => ({
  name: 'Dev Jr. League Registration Confirmation',
  seasonTbd: false,
  anticipatedStart: '2026-08-01',
  anticipatedEnd: '2026-08-10',
  programLocation: { facility: { name: 'Dev Facility (Preview)' } },
})

const buildRegionalProgram = () => ({
  name: 'Dev Jr. League Regional Confirmation',
})

const buildChampionshipProgram = () => ({
  name: 'Dev Jr. League Championship Confirmation',
  playDays: [{ date: '2026-09-14' }],
  events: [{ date: '2026-09-15' }],
})

const getProgramDates = program => {
  const allEvents = program.playDays.concat(program.events)
  if (allEvents.length === 0) return ''
  const { startDate, endDate } = eventsRange(allEvents)
  return formatDateRange(startDate, endDate, {
    dateFormat: 'MMMM yyyy',
    sameYearDateFormat: 'MMMM',
  })
}

export default function MerchPreview() {
  const { flow } = useParams()
  const auth = useAuth()
  const email = auth?.user?.email || 'dev-user@example.com'
  const classes = useStyles()

  if (!safeIsDevPreview()) return null

  const normalizedFlow = flow || 'register'

  const wrap = inner => (
    <Container maxWidth="sm">
      <Paper>
        <Box p={[4, 7, 7]} mb={2}>
          {inner}
        </Box>
      </Paper>
    </Container>
  )

  if (normalizedFlow === 'regional') {
    const program = buildRegionalProgram()
    return wrap(
      <Grid container spacing={4} direction="column" className={classes.grid}>
        <Grid item xs={12}>
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            style={{ fontWeight: 'bold' }}
          >
            {program.name}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography align="center">A receipt has been sent to:</Typography>
          <Typography align="center">{email}</Typography>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <JrlMerchCarousel />
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <Box className={classes.badgeContainer}>
            <a href={MY_PGA_GOOGLE_PLAY_STORE} target="_blank" rel="noreferrer">
              <img
                src={GooglePlayBadge}
                alt="MyPGA app on Google Play"
                className={classes.badge}
              />
            </a>
            <a href={MY_PGA_APPLE_APP_STORE} target="_blank" rel="noreferrer">
              <img
                src={AppStoreBadge}
                alt="MyPGA app on App Store"
                className={classes.badge}
              />
            </a>
          </Box>
        </Grid>
      </Grid>,
    )
  }

  if (normalizedFlow === 'championship-season') {
    const program = buildChampionshipProgram()
    const programDates = getProgramDates(program)

    return wrap(
      <Grid container spacing={4} direction="column" className={classes.grid}>
        <Grid item xs={12}>
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            style={{ fontWeight: 'bold' }}
          >
            {program.name}
          </Typography>
          {programDates && (
            <Typography align="center">{programDates}</Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <Typography align="center">A receipt has been sent to:</Typography>
          <Typography align="center">{email}</Typography>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <JrlMerchCarousel />
        </Grid>
      </Grid>,
    )
  }

  // Default: "register" style confirmation
  const program = buildRegisterProgram()
  const programDates = program.seasonTbd
    ? 'TBD'
    : formatDateRange(program.anticipatedStart, program.anticipatedEnd, {
      dateFormat: 'MMMM yyyy',
      sameYearDateFormat: 'MMMM',
    })

  return wrap(
    <Grid container spacing={4} direction="column" className={classes.grid}>
      <Grid item xs={12}>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          style={{ fontWeight: 'bold' }}
        >
          {program.name}
        </Typography>
        <Typography align="center">
          {program.programLocation.facility.name} | {programDates}
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Typography align="center">A receipt has been sent to:</Typography>
        <Typography align="center">{email}</Typography>
      </Grid>

      <Grid item xs={12}>
        <Divider />
      </Grid>

      <Grid item xs={12}>
        <Typography>
          Dev preview mode: the merch carousel shown below matches the real
          Junior League confirmation step.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Divider />
      </Grid>

      <Grid item xs={12}>
        <JrlMerchCarousel />
      </Grid>

      <Grid item xs={12}>
        <Divider />
      </Grid>

      <Grid item xs={12}>
        <Box className={classes.badgeContainer}>
          <a href={MY_PGA_GOOGLE_PLAY_STORE} target="_blank" rel="noreferrer">
            <img
              src={GooglePlayBadge}
              alt="MyPGA app on Google Play"
              className={classes.badge}
            />
          </a>
          <a href={MY_PGA_APPLE_APP_STORE} target="_blank" rel="noreferrer">
            <img
              src={AppStoreBadge}
              alt="MyPGA app on App Store"
              className={classes.badge}
            />
          </a>
        </Box>
      </Grid>
    </Grid>,
  )
}
